<template>
    <div
        class="ask-pendo-context-button"
        :class="{ 'ask-pendo-context-button--icon-only': iconOnly }">
        <pendo-button
            v-if="!iconOnly"
            theme="app"
            type="secondary"
            icon="sparkles"
            :label="$t('askPendo.Ask agent mode')"
            :disabled="disabled"
            pendo-feature="ask-pendo-context-button"
            @click="openConversationDrawer" />
        <pendo-icon-button
            v-else
            class="ask-pendo-context-button__icon-button"
            theme="app"
            type="secondary"
            icon="sparkles"
            icon-size="16"
            :tooltip="$t('askPendo.Ask agent mode')"
            :disabled="disabled"
            pendo-feature="ask-pendo-context-button"
            @click="openConversationDrawer" />

        <pendo-drawer
            :visible="isDrawerOpen"
            :size="drawerSize"
            push=".vue-wrapper"
            @close="closeConversationDrawer">
            <template #header>
                <div class="ask-pendo-context-button__header">
                    <span class="ask-pendo-context-button__title">
                        {{ $t('askPendo.Agent mode') }}
                    </span>
                    <pendo-drawer-close />
                </div>
            </template>
            <template #body>
                <div class="ask-pendo-context-button__body">
                    <ask-pendo-conversation
                        v-if="conversationId"
                        :id="conversationId"
                        :key="conversationId"
                        @artifact-pane-change="onArtifactPaneChange" />
                </div>
            </template>
        </pendo-drawer>
    </div>
</template>

<script>
import { PendoButton, PendoIconButton, PendoDrawer, PendoDrawerClose } from '@pendo/components';
import { mapActions } from 'vuex';
import { v4 as uuid } from 'uuid';
import AskPendoConversation from '@/ask-pendo/ask-pendo-conversation.vue';
import { getInContextPrompt } from '../utils/in-context-prompt-utils';

export default {
    name: 'AskPendoContextButton',
    components: {
        AskPendoConversation,
        PendoButton,
        PendoIconButton,
        PendoDrawer,
        PendoDrawerClose,
    },
    props: {
        countableType: {
            type: String,
            required: true,
        },
        countableId: {
            type: String,
            required: true,
        },
        countableName: {
            type: String,
            required: true,
        },
        suggestedQuestions: {
            type: Array,
            default: () => [],
        },
        iconOnly: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /** When provided, called on click to get conversation id instead of creating with prompt. Enables custom flows (e.g. table artifact). */
        getConversationId: {
            type: Function,
            default: null,
        },
    },
    emits: ['open-conversation-drawer', 'close-conversation-drawer'],
    data () {
        return {
            isDrawerOpen: false,
            conversationId: null,
            drawerSize: '448px',
        };
    },
    watch: {
        $route () {
            if (this.isDrawerOpen) {
                this.isDrawerOpen = false;
                this.$emit('close-conversation-drawer');
            }
            this.conversationId = null;
            this.drawerSize = '448px';
        },
    },
    methods: {
        ...mapActions('askPendo', ['createConversation']),
        async openConversationDrawer () {
            this.$emit('open-conversation-drawer');
            let conversationId;
            if (this.getConversationId) {
                conversationId = await this.getConversationId();
            } else {
                const prompt = this.generatePrompt();
                conversationId = uuid();
                const artifacts = {};
                for (const question of this.suggestedQuestions) {
                    artifacts[uuid()] = {
                        type: 'next_actions',
                        action_type: 'ask_question',
                        data: {
                            question,
                            prompt,
                        },
                    };
                }
                const metadata = {
                    heading_type: 'ask_question',
                    artifact_ids: Object.keys(artifacts),
                };
                await this.createConversation({ id: conversationId, prompt, metadata, artifacts });
            }

            if (conversationId) {
                this.conversationId = conversationId;
                this.isDrawerOpen = true;
            }
        },
        generatePrompt () {
            return getInContextPrompt(this.countableType, this.countableId, this.countableName);
        },
        closeConversationDrawer () {
            this.isDrawerOpen = false;
            this.drawerSize = '448px';
            this.$emit('close-conversation-drawer');
        },
        onArtifactPaneChange (artifactPaneVisible) {
            this.drawerSize = artifactPaneVisible ? '720px' : '448px';
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-context-button {
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__title {
        font-weight: 600;
        font-size: 14px;
    }

    &__body {
        position: relative;
        height: 100%;
        min-height: 640px;
    }

    &--icon-only :deep(.ask-pendo-context-button__icon-button) {
        svg {
            transition: transform 0.5s ease-in-out;
        }

        &:hover:not(.is-disabled) svg {
            transform: rotate(360deg);
        }
    }
}

:deep(.pendo-drawer__body) {
    padding: 0;
    overflow: hidden;
}
</style>
